/**
 * Task details: description, metadata, contributor-entered GitHub
 * references, the append-only history, and one role-aware primary action.
 *
 * The action buttons come from the server's `availableTransitions` — the
 * permission mirror — so the page never derives an offer from the role. Each
 * action opens a `ConfirmDialog` naming what will happen; the mutation
 * carries the task version the person was looking at, and a conflict renders
 * the shared "this task changed" state rather than overwriting anybody.
 *
 * One idempotency key is minted per dialog *opening*, not per click, so a
 * double-click or a retry after a network failure replays rather than
 * double-applies.
 */

import { useCallback, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchTask } from '../api.js';
import { useWorkspaceCopy } from '../components.js';
import { useToasts } from '../ui.js';
import { useSession } from '../session.js';
import { FailurePanel, LoadingPanel, useAsync } from './common.js';
import { NotAvailablePage } from './NotAvailablePage.js';
import { TaskActionButtons } from './task-detail/TaskActionButtons.js';
import { TaskDependencySections } from './task-detail/TaskDependencySections.js';
import { TaskDescriptionAndHistory } from './task-detail/TaskDescriptionAndHistory.js';
import { TaskDetailHeader } from './task-detail/TaskDetailHeader.js';
import { TaskEditDialog } from './task-detail/TaskEditDialog.js';
import { TaskMetadata } from './task-detail/TaskMetadata.js';
import { TaskRecordDialogs } from './task-detail/TaskRecordDialogs.js';
import { TaskReferencesSection } from './task-detail/TaskReferencesSection.js';
import { TaskTransitionDialogs } from './task-detail/TaskTransitionDialogs.js';
import { useTaskDialogFields } from './task-detail/useTaskDialogFields.js';
import { useTaskDialogs } from './task-detail/useTaskDialogs.js';

export function TaskDetailPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { id } = useParams<{ id: string }>();
  const { announce } = useToasts();
  const { session } = useSession();
  const navigate = useNavigate();

  const load = useCallback(() => fetchTask(id ?? ''), [id]);
  const { state, reload } = useAsync(load);

  const fields = useTaskDialogFields();

  const mayUseDataRoom =
    session.status === 'authenticated' && session.profile.mayUseDataRoom;

  const {
    dialog,
    busy,
    actionFailure,
    failureAlert,
    loadDependencyCandidates,
    openDialog,
    closeDialog,
    act,
  } = useTaskDialogs({ copy, announce, navigate, reload, mayUseDataRoom, fields });

  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed') {
    // Missing and forbidden arrive identically; both land on the shared page.
    if (state.failure.kind === 'missing') return <NotAvailablePage />;
    return <FailurePanel failure={state.failure} onRetry={reload} />;
  }

  const view = state.value;
  const task = view.task;
  // What an approval may re-type the task as: the caller's own visible
  // types, which for the only role that can approve is both of them. Still
  // read from the profile rather than hard-coded, so the mirror stays a
  // mirror.
  const approvableTypes =
    session.status === 'authenticated'
      ? session.profile.visibleTaskTypes
      : ([] as readonly string[]);

  return (
    <>
      <TaskDetailHeader task={task} />

      {failureAlert !== null && dialog === null ? failureAlert : null}

      <div className="mj-task-layout">
        <TaskDescriptionAndHistory view={view} />

        <aside className="mj-task-sidebar">
          <TaskActionButtons view={view} openDialog={openDialog} />
          <TaskMetadata task={task} />
          <TaskReferencesSection task={task} />
          <TaskDependencySections view={view} />
        </aside>
      </div>

      {/* ----------------------------- dialogs ----------------------------- */}

      <TaskTransitionDialogs
        view={view}
        dialog={dialog}
        busy={busy}
        actionFailure={actionFailure}
        failureAlert={failureAlert}
        closeDialog={closeDialog}
        act={act}
        fields={fields}
        mayUseDataRoom={mayUseDataRoom}
        approvableTypes={approvableTypes}
      />
      <TaskEditDialog
        view={view}
        dialog={dialog}
        busy={busy}
        actionFailure={actionFailure}
        failureAlert={failureAlert}
        closeDialog={closeDialog}
        act={act}
        fields={fields}
        approvableTypes={approvableTypes}
        loadDependencyCandidates={loadDependencyCandidates}
      />
      <TaskRecordDialogs
        view={view}
        dialog={dialog}
        busy={busy}
        actionFailure={actionFailure}
        failureAlert={failureAlert}
        closeDialog={closeDialog}
        act={act}
        fields={fields}
        loadDependencyCandidates={loadDependencyCandidates}
      />
    </>
  );
}
